import React, { useState } from 'react';
import {
  Navigate, Route, Routes, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import LoginPage from './login/LoginPage';
import useQuery from './common/util/useQuery';
import { useEffectAsync } from './reactHelper';
import App from './App';
import Landing from './shell/Landing';
import OverviewPage from './overview/OverviewPage';
import TripsPage from './trips/TripsPage';
import AnalyticsPage from './analytics/AnalyticsPage';
import FleetInsightsPage from './operations/FleetInsightsPage';
import AreaOccupancyPage from './operations/AreaOccupancyPage';
import BetweenAreasPage from './operations/BetweenAreasPage';
import AdministrationPage from './operations/AdministrationPage';
import PublicTrackingPage from './operations/PublicTrackingPage';
import VerifyPage from './login/VerifyPage';
import OperationalReportsPage from './operations/OperationalReportsPage';
import AccountPage from './operations/AccountPage';
import VehicleRegistrationPage from './operations/VehicleRegistrationPage';
import YardIntakePage from './operations/YardIntakePage';
import TrackingPage from './operations/TrackingPage';
import LiveTrackingPage from './operations/LiveTrackingPage';
import VehicleReplayPage from './operations/VehicleReplayPage';
import ParkingQueuePage from './operations/ParkingQueuePage';
import ShiftDeskPage from './operations/ShiftDeskPage';

// Tracking links are shared from trip detail now. Old Shared Tracking bookmarks land on the
// same trip in Gate Operations instead of a page that is no longer in the navigation.
const SharedTrackingRedirect = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/gate-operations/${id}` : '/gate-operations'} replace />;
};

const Navigation = () => {
  const navigate = useNavigate();

  const [redirectsHandled, setRedirectsHandled] = useState(false);

  const { pathname } = useLocation();
  const query = useQuery();

  useEffectAsync(async () => {
    if (query.get('token')) {
      const token = query.get('token');
      await fetch(`/api/session?token=${encodeURIComponent(token)}`);
      navigate(pathname);
    } else {
      setRedirectsHandled(true);
    }
  }, [query]);

  if (!redirectsHandled) {
    return (<LinearProgress />);
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* A tenant's own sign-in URL. Same page and same credentials as /login —
          the slug only selects which name and logo the page wears. */}
      <Route path="/t/:slug" element={<LoginPage />} />
      <Route path="/track/:token" element={<PublicTrackingPage />} />
      <Route path="/verify/:token" element={<VerifyPage />} />
      <Route path="/" element={<App />}>
        <Route index element={<Landing />} />
        <Route path="gate-operations" element={<TripsPage key="gate" view="gate" />} />
        <Route path="gate-operations/:id" element={<TripsPage key="gate" view="gate" />} />
        <Route path="vehicles" element={<AdministrationPage key="vehicles" area="vehicles" />} />
        <Route path="vehicles/register" element={<VehicleRegistrationPage />} />
        <Route path="parking" element={<ParkingQueuePage />} />
        <Route path="yard-intake" element={<YardIntakePage />} />
        <Route path="shift-desk" element={<ShiftDeskPage />} />
        <Route path="plant-tracking" element={<TrackingPage key="plant" mode="plant" />} />
        <Route path="plant-tracking/:id" element={<TripsPage key="plant" view="plant" />} />
        <Route path="outbound-tracking" element={<TrackingPage key="outbound" mode="outbound" />} />
        <Route path="outbound-tracking/:id" element={<TripsPage key="outbound" view="outbound" />} />
        <Route path="vehicle-replay" element={<VehicleReplayPage />} />
        <Route path="shared-tracking" element={<SharedTrackingRedirect />} />
        <Route path="shared-tracking/:id" element={<SharedTrackingRedirect />} />
        <Route path="outbound-operations" element={<OverviewPage key="outbound" view="outbound" />} />
        <Route path="area-movement" element={<OverviewPage key="areas" view="areas" />} />
        <Route path="exceptions" element={<OverviewPage exceptionsOnly />} />
        <Route path="configuration" element={<AdministrationPage key="configuration" area="configuration" />} />
        <Route path="live" element={<LiveTrackingPage />} />
        <Route path="trips" element={<TripsPage />} />
        <Route path="trips/:id" element={<TripsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="fleet-insights" element={<FleetInsightsPage />} />
        <Route path="inside-areas" element={<AreaOccupancyPage />} />
        <Route path="between-areas" element={<BetweenAreasPage />} />
        <Route path="administration" element={<AdministrationPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="reports" element={<OperationalReportsPage />} />
      </Route>
      {/* Everything this product has is listed above. The tracking application it was forked from
          also shipped self-registration, password reset, a change-server screen, device and user
          administration, geofence editing, replay, and position and event inspectors, plus seven
          legacy report pages and its own live map. Those screens spoke a different vocabulary —
          devices rather than trucks, geofences rather than areas — and read a data layer this
          product no longer fills. Live Tracking above replaces that map on this product's own
          bootstrap and map component; the rest are answered by the screens listed here. Any other
          path lands on the overview. */}
      {/* Tyre monitoring. Declared here, even though App decides by role what to render, because
          a path this table does not know is redirected to "/" by the catch-all below - and then the
          tyre shell renders at a path none of its own pages match, which is a working header above
          an empty page. The splat keeps /tpms/alerts and /tpms/vehicles/123 intact. */}
      <Route path="/tpms/*" element={<App />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default Navigation;
